/**
 * CapabilitiesVertical Component - By John Bellew - Infinia.ie - last updated 10/07/2025   
 * 
 * A vertical display component for showcasing IFIA capabilities organized by categories.
 * Features include:
 * - Category-based grouping with color-coded visual indicators
 * - Interactive navigation cards with hover tooltips
 * - Responsive design with IFIA brand color scheme
 * - Smooth navigation integration with capability detail pages
 * - Professional card layout with custom styling
 * - Memoized data processing for optimal performance
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of capability objects containing id, title, and category
 * @returns {JSX.Element} Vertical capabilities display component
 */
import React from 'react';
import './CapabilitiesVertical.css';
import { NavLink } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

/**
 * Capabilities Component
 * 
 * Displays capabilities in a vertical layout with category-based grouping.
 * Each capability is rendered as a clickable card with tooltip support.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of capability data objects
 * @returns {JSX.Element} Capabilities display component
 */
function Capabilities({ data }) {

    // Group items by category
    const groupedData = React.useMemo(() => {
        if (!data) return [];

        const groups = data.reduce((acc, item) => {
            if (!acc[item.category]) {
                acc[item.category] = {
                    title: item.category,
                    items: []
                };
            }
            acc[item.category].items.push(item);
            return acc;
        }, {});

        return Object.values(groups);
    }, [data]);

    return (
        <div className="capabilities-layout">
            <div className="capabilities-wrapper">
                <div className="capabilities-container">
                    {groupedData.map(category => (
                        <div key={category.title} className="category-section">
                            <div
                                className="legend-title"
                                style={{ borderRight: `25px solid ${getCategoryColor(category.title)}`}}
                            >
                                {category.title}
                            </div>
                            {category.items.map(item => (
                                <Tippy content={item.title}>
                                    <NavLink
                                        key={item.id}
                                        to={`/CapabilityInnovation/Capability/${item.id}`}
                                        className="capability-card"
                                        style={{ borderRightColor: getCategoryColor(category.title) }}
                                    >
                                        {item.title}
                                    </NavLink> 
                                </Tippy>
                            ))}
                      
                        </div>
                        
                    ))}
               
                </div>

            </div>
        </div>
    );
}

/**
 * Helper function to get IFIA brand colors for capability categories
 * 
 * Maps capability categories to their corresponding IFIA brand colors
 * for consistent visual styling across the application.
 * 
 * @param {string} category - The capability category name
 * @returns {string} Hex color code for the category
 */
function getCategoryColor(category) {
    switch (category) {
        case "Class of Product": return "#eb7b15"; // var(--ifia-orange)
        case "Business Area": return "#843390"; // var(--ifia-purple)
        case "Legal Structure": return "#56b32f"; // var(--ifia-green)
        case "Business Environment": return "#3f47cc";
        case "Regulatory Structure": return "#9ad9ea";
        default: return "#000000";
    }
}

export default Capabilities; 